import type { QuickFillItem } from '../../../shared/types';
import type { BulkUpdateResult } from '../../../shared/services/quickFillRevitService';
import type { QuickFillNavigationMarker } from './components/QuickFillNavigationDock';

const clampPreviewPosition = (value: number, max: number): number =>
  Math.min(Math.max(value, 0), Math.max(max, 0));

const sortRowIndices = (rowIndices: number[]): number[] =>
  [...rowIndices].sort((left, right) => left - right);

export const toggleRowIndex = (rowIndices: number[], rowIndex: number): number[] =>
  rowIndices.includes(rowIndex)
    ? rowIndices.filter((existing) => existing !== rowIndex)
    : sortRowIndices([...rowIndices, rowIndex]);

export const areRowIndicesEqual = (left: number[], right: number[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

export const buildNavigationMarkers = (
  navigationMarkerRows: number[],
  quickFillData: QuickFillItem[],
  filteredRowPositionMap: Map<number, number>
): QuickFillNavigationMarker[] =>
  sortRowIndices(
    navigationMarkerRows.filter((rowIndex) => rowIndex >= 0 && rowIndex < quickFillData.length)
  ).map((rowIndex) => {
    const position = filteredRowPositionMap.get(rowIndex);
    return {
      rowIndex,
      label: quickFillData[rowIndex]?.Name || `Row ${rowIndex + 1}`,
      position: position ?? null,
      isVisible: position != null,
    };
  });

export const buildNavigatorRowCoverage = (
  filteredRowIndices: number[],
  quickFillData: QuickFillItem[],
  visiblePropertyLabels: string[]
): number[] => {
  if (visiblePropertyLabels.length === 0) {
    return filteredRowIndices.map(() => 0);
  }

  return filteredRowIndices.map((rowIndex) => {
    const row = quickFillData[rowIndex];
    if (!row) {
      return 0;
    }

    const populatedCount = visiblePropertyLabels.reduce((count, label) => {
      const value = row[label as keyof QuickFillItem];
      return typeof value === 'string' && value.trim() !== '' ? count + 1 : count;
    }, 0);

    return populatedCount / visiblePropertyLabels.length;
  });
};

export const pickPreferredQuickFillLabel = (
  labels: string[],
  preferredLabels: string[]
): string | null =>
  preferredLabels.find((label) => labels.includes(label)) ?? labels[0] ?? null;

export const PREVIEW_AUTO_FILL_DIALOG_STATE = {
  mode: 'all' as const,
  preview: {
    matches: 4,
    total: 6,
    preview: 'Auto-fill found 4 suggested updates across 6 empty cells based on repeated family naming patterns.',
  },
};

export const PREVIEW_SAVE_REVIEW_RESULT: BulkUpdateResult = {
  totalUpdates: 3,
  successCount: 2,
  failureCount: 1,
  results: [
    { familyType: 'Valve - Generic:Standard', parameter: 'OBJECT-TYPE', value: 'VALVE', success: true },
    { familyType: 'Valve - Generic:Standard', parameter: 'SKEY', value: 'GAT', success: true },
    { familyType: 'Cap - Generic:Standard', parameter: 'OBJECT-TYPE', value: 'CAP', success: false, error: 'Target parameter is read-only in Revit' },
  ],
};

export const PREVIEW_SAVE_REVIEW_WARNING =
  '1 row still needs manual review.';

interface ResolveQuickFillTourNavigationStateArgs {
  shouldForceOpenNavigator: boolean;
  navigationDockCollapsed: boolean;
  activeNavigationRowIndex: number | null;
  quickFillData: QuickFillItem[];
  filteredRowIndices: number[];
  filteredRowPositionMap: Map<number, number>;
  markerItems: QuickFillNavigationMarker[];
  hiddenMarkerCount: number;
  activeNavigationRowLabel: string | null;
  activeRowMarked: boolean;
  canJumpToActive: boolean;
  canToggleMarker: boolean;
  currentNavigationPosition: number | null;
}

export interface QuickFillTourNavigationState {
  isPreviewState: boolean;
  previewNavigationPosition: number | null;
  collapsed: boolean;
  markers: QuickFillNavigationMarker[];
  hiddenMarkerCount: number;
  activeRowLabel: string | null;
  activeRowMarked: boolean;
  canJumpToActive: boolean;
  canToggleMarker: boolean;
  currentPosition: number | null;
}

export const resolveQuickFillTourNavigationState = ({
  shouldForceOpenNavigator,
  navigationDockCollapsed,
  activeNavigationRowIndex,
  quickFillData,
  filteredRowIndices,
  filteredRowPositionMap,
  markerItems,
  hiddenMarkerCount,
  activeNavigationRowLabel,
  activeRowMarked,
  canJumpToActive,
  canToggleMarker,
  currentNavigationPosition,
}: ResolveQuickFillTourNavigationStateArgs): QuickFillTourNavigationState => {
  const previewRowIndex =
    shouldForceOpenNavigator && activeNavigationRowIndex == null && quickFillData.length > 0
      ? filteredRowIndices[1] ?? filteredRowIndices[0] ?? clampPreviewPosition(1, quickFillData.length - 1)
      : null;
  const isPreviewState = previewRowIndex != null;
  const previewNavigationPosition =
    previewRowIndex == null
      ? null
      : filteredRowPositionMap.get(previewRowIndex) ?? clampPreviewPosition(previewRowIndex, quickFillData.length - 1);
  const previewMarkers: QuickFillNavigationMarker[] =
    previewRowIndex == null
      ? []
      : [
          {
            rowIndex: previewRowIndex,
            label: quickFillData[previewRowIndex]?.Name || `Row ${previewRowIndex + 1}`,
            position: previewNavigationPosition,
            isVisible: true,
          },
        ];

  return {
    isPreviewState,
    previewNavigationPosition,
    collapsed: shouldForceOpenNavigator ? false : navigationDockCollapsed,
    markers: isPreviewState ? previewMarkers : markerItems,
    hiddenMarkerCount: isPreviewState ? 0 : hiddenMarkerCount,
    activeRowLabel: isPreviewState ? previewMarkers[0]?.label ?? null : activeNavigationRowLabel,
    activeRowMarked: isPreviewState ? true : activeRowMarked,
    canJumpToActive: isPreviewState ? previewNavigationPosition != null : canJumpToActive,
    canToggleMarker: isPreviewState ? true : canToggleMarker,
    currentPosition: isPreviewState ? previewNavigationPosition : currentNavigationPosition,
  };
};
